import { Component, h } from 'preact';
import { route } from 'preact-router';
import { Subscription } from 'rxjs';
import { map, distinctUntilChanged, tap, filter } from 'rxjs/operators';

import getStore, { appState$ } from '../../../store';
import * as InquiriesActions from '../../../reducers/inquiries/inquiries.actions';

import { Inquiry, InquiryDocument, InquiryFTF } from '../../../../../types/inquiries';
import { InquiryDocumentType, INQUIRY_PAGE_STAGES } from '../../../../../constants/inquiries';

import { SetFirstCallModal } from '../actions/set-first-call-modal.component';
import { ScheduleViewingModal } from '../actions/schedule-viewing-modal.component';
import { ViewingListModal } from '../viewing-list/viewing-list-modal';
import { InqDocumentModalHost } from '../documents/inq-document-modal-host.component';
import { InqOfferSentModalHost } from '../documents/inq-offer-sent-modal-host.component';
import { InqOfferStatusModal } from '../documents/inq-offer-status-modal.component';
import { InqOfferAcceptedModalHost } from '../documents/inq-offer-accepted-modal-host.component';
import { InqUploadOfferModalHost } from '../documents/inq-upload-offer-modal-host.component';
import { InqUploadContractModalHost } from '../documents/inq-upload-contract-modal-host.component';
import { InqEditCollectionAmountModal } from '../collection/inq-edit-collection-amount-modal.component';
import { InqEditInvoiceNumberModal } from '../collection/inq-edit-invoice-number-modal.component';
import { InqEditCommissionAmountModal } from '../collection/inq-edit-commission-amount-modal.component';
import { CommentsModal } from '../../listings/comments-modal';
import { AddClientModalHost } from '../add-client/add-client-modal-host.component';
import { FastTrackModalMode } from '../add-client/mode';


interface InquiryGridModalsState {
  selectedInquiry?: Inquiry;
  visibleModal?: string;
  documentForEditing?: InquiryDocument;
  ftfForEditing?: InquiryFTF;
  selectedVlKey?: string;
}

const store = getStore();

export class InquiryGridModals extends Component<any, InquiryGridModalsState> {
  subscriptions: Subscription[] = [];
  
  componentDidMount() {
    console.log('InquiryGridModals: mounted');
    const inquiries$ = appState$.pipe(
      map(appState => appState.inquiries),
      filter(inquiries => !!inquiries)
    );
    
    inquiries$.pipe(
      map(inquiries => {
        const { selectedInquiry, visibleModal, documentForEditing, ftfForEditing, selectedVlKey } = inquiries;
        return { selectedInquiry, visibleModal, documentForEditing, ftfForEditing, selectedVlKey } as InquiryGridModalsState;
      }),
      distinctUntilChanged(),
      // tap(console.log)
    ).subscribe(state => this.setState(state));
  }

  closeInquiryActionModal() {
    store.dispatch(InquiriesActions.hideModal());
  }

  onViewFtf(inquiry: Inquiry) {
    this.closeInquiryActionModal();
    store.dispatch(InquiriesActions.selectInquiry(inquiry));
  }

  loadAllStages() {
    store.dispatch(InquiriesActions.loadAllStages());
  }

  onStageTransition(targetStage: string) {
    store.dispatch(InquiriesActions.loadAllStages());
    store.dispatch(InquiriesActions.hideModal());
    route(`/kumita-brokers/inquiries/${targetStage}`);
  }

  onFtfSaveProceed() {
    // Note that it's called Collections on the frontend, but it's still called
    // "For Collection" in the backend.
    const selectedInquiry = this.state.selectedInquiry;
    let targetStageName = selectedInquiry.stage;

    if (targetStageName === 'For Collection') {
      targetStageName = 'Collections';
    }

    this.closeInquiryActionModal();

    const stageDefn = INQUIRY_PAGE_STAGES.find(stage => stage.name === targetStageName);
    route(`/kumita-brokers/inquiries/${stageDefn.slug}`);
  }

  openDialog(dialogId: string, selectedInquiry: Inquiry, documentForEditing?: InquiryDocument) {
    const action = InquiriesActions.showModal(dialogId, { selectedInquiry, documentForEditing });
    store.dispatch(action);
  }

  wireToDialog(dialogId: string) {
    return (inquiry: Inquiry) => this.openDialog(dialogId, inquiry);
  }

  renderModal(props, state: InquiryGridModalsState) {
    const visibleModal = state.visibleModal;
    const inquiryCode = state.selectedInquiry && state.selectedInquiry.inquiryCode;
    
    switch (visibleModal) {
      case 'add-client':
        return (
          <AddClientModalHost
            mode={ FastTrackModalMode.ADD_CLIENT }
            onProceed={ () => this.onStageTransition('accepted') }
            onClose={ this.closeInquiryActionModal } />
        );
      case 'edit-ftf':
        return (
          <AddClientModalHost
            mode={ FastTrackModalMode.FTF }
            inquiry={ state.selectedInquiry }
            ftf={ state.ftfForEditing }

            onProceed={ () => this.onFtfSaveProceed() }
            onClose={ this.closeInquiryActionModal } />
        )
      case 'comments': 
        return (
          <CommentsModal
            type="inquiry"
            onClose={ this.closeInquiryActionModal }
            keycode={ inquiryCode } />
        )
      case 'set-first-call': {
        return (
          <SetFirstCallModal
            inquiry={ state.selectedInquiry } 
            onClose={ this.closeInquiryActionModal } />  
        );
      }
      case 'schedule-viewing': {
        return (
          <ScheduleViewingModal
            inquiry={ state.selectedInquiry }
            onClose={ this.closeInquiryActionModal }
          />
        );
      }
      case 'create-vl':
        return (
          <ViewingListModal inquiry={ state.selectedInquiry }
            selectedVlKey={ state.selectedVlKey }
            onViewFtf={ inquiry => this.onViewFtf(inquiry) }
            onClose={ this.closeInquiryActionModal }
            onScheduleViewing={ this.wireToDialog('schedule-viewing') }
            onStageTransition={ targetStage => this.onStageTransition(targetStage) } />
        );
      case 'create-loi':
      case 'edit-loi':
        return (
          <InqDocumentModalHost inquiry={ state.selectedInquiry }
            documentType={ InquiryDocumentType.OFFER }
            docItemToEdit={ state.documentForEditing }
            onProceedToFileClick={ document => this.openDialog('upload-offer-sent', state.selectedInquiry, document) }
            onClose={ this.closeInquiryActionModal } />
        );
      case 'upload-offer-sent':
        return (
          <InqOfferSentModalHost onClose={ this.closeInquiryActionModal }
            documentType={ InquiryDocumentType.OFFER }
            inquiry={ state.selectedInquiry } />
        );
      case 'offer-status':
        return (
          <InqOfferStatusModal onClose={ this.closeInquiryActionModal }
            inquiry={ state.selectedInquiry }
            document={ state.documentForEditing } />
        );
      case 'upload-offer-accepted':
        return (
          <InqOfferAcceptedModalHost onClose={ this.closeInquiryActionModal }
            inquiry={ state.selectedInquiry } />
        );
      case 'upload-offer-generic':
        return (
          <InqUploadOfferModalHost onClose={ this.closeInquiryActionModal }
            inquiry={ state.selectedInquiry }/>
        )
      case 'create-contract':
      case 'edit-contract':
        return (
          <InqDocumentModalHost onClose={ this.closeInquiryActionModal }
            documentType={ InquiryDocumentType.CONTRACT }
            docItemToEdit={ state.documentForEditing }
            inquiry={ state.selectedInquiry }
            onProceedToFileClick={ doc => this.openDialog('upload-contract', state.selectedInquiry, doc) } />
        );
      case 'upload-contract':
        return (
          <InqUploadContractModalHost onClose={ this.closeInquiryActionModal }
            inquiry={ state.selectedInquiry } />
        );
      case 'edit-collection-amount':
        return (
          <InqEditCollectionAmountModal onClose={ this.closeInquiryActionModal }
            inquiry={ state.selectedInquiry }
            onAmountUpdateSuccess={ () => this.loadAllStages() } />
        );
      case 'edit-invoice-number':
        return (
          <InqEditInvoiceNumberModal onClose={ this.closeInquiryActionModal }
            inquiry={ state.selectedInquiry }
            onInvoiceUpdateSuccess={ () => this.loadAllStages() } />
        );
        case 'edit-commission':
          return (
            <InqEditCommissionAmountModal onClose={ this.closeInquiryActionModal }
              inquiry={ state.selectedInquiry }
              onUpdateSuccess={ () => this.loadAllStages() } />
          );
          case 'edit-commission-card':
            return (
              <InqEditCommissionAmountModal onClose={ this.closeInquiryActionModal }
                inquiry={ state.selectedInquiry }
                onUpdateSuccess={ () => location.reload() } />
            );
        }
  }

  render() {
    return this.renderModal(this.props, this.state);
  }
}